import type { CaseSheetEditableFields, DiagnosisCodeItem } from '@/components/views/CaseSheetView';
import type { StructuredMedication } from '@/lib/utils/medications';
import { parseMedicationsToTable, type ConclusionSection } from '@/lib/utils/conclusionParser';
import type { PrescribedMedicine, PersonalHistory } from '@/ayurveda/types/ayurveda';
import {
  getFamilyHistoryItems,
  splitCommaSeparatedText,
  splitMedicalNarrativeToBullets,
  splitProseToBullets,
} from '@/lib/utils/clinicalText';

export interface CaseSheetSummaryInput {
  patient: {
    name: string;
    identifier: string;
    age: number;
    gender: string;
    dateOfBirth?: string;
    phoneNumber?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
  };
  consultationDate: string;
  fields: CaseSheetEditableFields;
  vitals?: {
    height?: number;
    weight?: number;
    temperature?: number;
    bloodPressure?: string;
    heartRate?: number;
    respiratoryRate?: number;
    oxygenSaturation?: number;
  };
  diet?: string[];
  lifestyle?: string[];
  viruddhaAhaara?: string[];
  /** Gates the Ayurveda-only Diagnosis/Treatment sections below — never set
   *  for other departments. */
  isAyurveda?: boolean;
  /** Structured medicines table, rendered instead of the plain-text
   *  `treatmentMedicines` field when present. Ayurveda only. */
  treatmentMedicinesTable?: PrescribedMedicine[];
  /** Diet/Appetite/Sleep/Exercise/Bowel/Bladder/Addiction/Menstrual. Ayurveda only. */
  personalHistory?: PersonalHistory | null;
}

const PERSONAL_HISTORY_FIELDS: Array<{ key: keyof PersonalHistory; label: string }> = [
  { key: 'diet', label: 'Diet' },
  { key: 'appetite', label: 'Appetite' },
  { key: 'sleep', label: 'Sleep' },
  { key: 'exercise', label: 'Exercise' },
  { key: 'bowel', label: 'Bowel' },
  { key: 'bladder', label: 'Bladder' },
  { key: 'addiction', label: 'Addiction' },
  { key: 'menstrual', label: 'Menstrual' },
];

/**
 * Formats medications as a text-based table for clipboard/PDF output.
 * Creates aligned columns for better readability in plain text.
 */
function formatMedicationsAsTable(medications: StructuredMedication[]): string {
  if (medications.length === 0) return '';

  // Define columns
  const columns = [
    { key: 'medication', label: 'Medication' },
    { key: 'dosage', label: 'Dosage' },
    { key: 'frequency', label: 'Frequency' },
    { key: 'route', label: 'Route' },
    { key: 'duration', label: 'Duration' },
    { key: 'relationWithMeal', label: 'With Meal' },
    { key: 'additionalInfo', label: 'Comments' },
    { key: 'itemCode', label: 'Item Code' },
  ];

  // Calculate column widths based on content
  const widths = columns.map((col) => {
    const maxDataWidth = Math.max(
      ...medications.map((med) => {
        const value = (med as any)[col.key] || '';
        return String(value).length;
      })
    );
    return Math.max(col.label.length, maxDataWidth);
  });

  // Build header row
  const headerRow = columns.map((col, i) => col.label.padEnd(widths[i])).join(' | ');
  const separator = widths.map((w) => '-'.repeat(w)).join('-+-');

  // Build data rows
  const dataRows = medications.map((med) => {
    return columns
      .map((col, i) => {
        const value = (med as any)[col.key] || '—';
        return String(value).padEnd(widths[i]);
      })
      .join(' | ');
  });

  return [headerRow, separator, ...dataRows].join('\n');
}

/**
 * Builds a structured plain-text summary of a case sheet consultation.
 * Used for both clipboard copy and PDF generation.
 */
export function buildCaseSheetSummaryText(input: CaseSheetSummaryInput): string {
  const {
    patient,
    consultationDate,
    fields,
    vitals,
    diet,
    lifestyle,
    viruddhaAhaara,
    isAyurveda,
    treatmentMedicinesTable,
    personalHistory,
  } = input;

  const lines: string[] = [];

  // Patient header
  lines.push('Case Sheet Summary');
  lines.push('='.repeat(60));
  lines.push('');
  lines.push(`Patient: ${patient.name}`);
  lines.push(`ID: ${patient.identifier}`);
  if (patient.dateOfBirth) lines.push(`Date of Birth: ${patient.dateOfBirth}`);
  if (patient.age >= 0) lines.push(`Age/Gender: ${patient.age}/${patient.gender}`);
  if (patient.phoneNumber) lines.push(`Phone: ${patient.phoneNumber}`);

  const addressParts = [
    patient.address,
    patient.city,
    patient.state,
    patient.postalCode,
    patient.country,
  ].filter(Boolean);
  if (addressParts.length > 0) lines.push(`Address: ${addressParts.join(', ')}`);

  lines.push(`Consultation Date: ${consultationDate}`);
  lines.push('');
  lines.push('-'.repeat(60));
  lines.push('');

  // Vitals
  if (vitals) {
    const vitalLines: string[] = [];
    if (vitals.height) vitalLines.push(`Height: ${vitals.height} cm`);
    if (vitals.weight) vitalLines.push(`Weight: ${vitals.weight} kg`);
    if (vitals.temperature) vitalLines.push(`Temperature: ${vitals.temperature} °F`);
    if (vitals.bloodPressure) vitalLines.push(`Blood Pressure: ${vitals.bloodPressure}`);
    if (vitals.heartRate) vitalLines.push(`Heart Rate: ${vitals.heartRate} bpm`);
    if (vitals.respiratoryRate) vitalLines.push(`Respiratory Rate: ${vitals.respiratoryRate} /min`);
    if (vitals.oxygenSaturation) vitalLines.push(`SpO2: ${vitals.oxygenSaturation}%`);

    if (vitalLines.length > 0) {
      lines.push('Vitals:');
      vitalLines.forEach((v) => lines.push(`  ${v}`));
      lines.push('');
    }
  }

  // Chief Complaint and Assessment/Diagnosis are each rendered as one bullet
  // per entry, splitting their comma-joined value (e.g. "Fever, chest pain,
  // and cough for 5 days") — tags stripped since this is a plain-text
  // (clipboard/PDF) export.
  const toBulletText = (value: string | undefined | null): string =>
    splitCommaSeparatedText(value)
      .map((item) => `• ${item.replace(/<[^>]+>/g, '')}`)
      .join('\n');
  const chiefComplaintText = toBulletText(fields.chiefComplaint);
  const assessmentDiagnosisText = toBulletText(fields.assessmentDiagnosis);
  const allergiesText = toBulletText(fields.allergies);
  const generalExaminationText = toBulletText(fields.generalExamination);
  const investigationsText = splitInvestigationsText(fields.investigations)
    .map((item) => `• ${item.replace(/<[^>]+>/g, '')}`)
    .join('\n');

  // Medical History is a free-text narrative, not an itemized list — split it
  // sentence-by-sentence plus per-sentence list-commas (see
  // splitMedicalNarrativeToBullets), and pull any embedded "Lab investigations
  // reviewed: ..." clause into its own bullet list instead of leaving the
  // whole narrative as one run-on paragraph.
  const medicalHistorySplit = splitMedicalNarrativeToBullets(fields.medicalHistory);
  const medicalHistoryText = medicalHistorySplit.mainItems.map((item) => `• ${item}`).join('\n');
  const labInvestigationsReviewedText = medicalHistorySplit.labItems
    .map((item) => `• ${item}`)
    .join('\n');

  // Every other prose field (Social History, Medication History, Review of
  // Systems, Treatment Plan, Doctor's Notes, etc.) — same sentence + list-comma
  // splitting as Medical History, minus its lab-clause detection, so a
  // multi-point narrative doesn't collapse into one run-on bullet.
  const toProseBulletText = (value: string | undefined | null): string =>
    splitProseToBullets(value)
      .map((item) => `• ${item}`)
      .join('\n');
  const clinicalSymptomsText = toProseBulletText(fields.clinicalSymptoms);
  const socialHistoryText = toProseBulletText(fields.socialHistory);
  const medicationHistoryText = toProseBulletText(fields.medicationHistory);
  const reviewOfSystemsText = toProseBulletText(fields.reviewOfSystems);
  const treatmentPlanText = toProseBulletText(fields.treatmentPlan);
  const adtOrderText = toProseBulletText(fields.adtOrder);
  const pediatricMilestoneText = toProseBulletText(fields.pediatricMilestone);
  const doctorNotesText = toProseBulletText(fields.doctorNotes);

  // Clinical sections — only include non-empty fields. Ayurveda skips
  // Medical/Family History, Assessment/Diagnosis, and Treatment Plan here —
  // they render below as the fixed Ayurveda History/Diagnosis/Treatment
  // sections instead, in the fixed Ayurveda case-sheet order.
  const sections: Array<{ label: string; value: string | undefined | null }> = [
    { label: 'Chief Complaint', value: chiefComplaintText },
    { label: 'Patient Summary', value: fields.patientSummary },
    { label: 'Clinical Symptoms', value: clinicalSymptomsText },
    ...(isAyurveda
      ? []
      : [
          { label: 'Medical History', value: medicalHistoryText },
          { label: 'Lab Investigations Reviewed', value: labInvestigationsReviewedText },
          { label: 'Family History', value: fields.familyHistory },
        ]),
    { label: 'Social History', value: socialHistoryText },
    { label: 'Allergies', value: allergiesText },
    { label: 'Medication History', value: medicationHistoryText },
    { label: 'Review of Systems', value: reviewOfSystemsText },
    { label: 'General Examination', value: generalExaminationText },
    ...(isAyurveda ? [{ label: 'Astha Sthaana Pareeksha', value: fields.doshaProfile }] : []),
    ...(isAyurveda
      ? []
      : [
          { label: 'Assessment / Diagnosis', value: assessmentDiagnosisText },
          { label: 'Treatment Plan', value: treatmentPlanText },
        ]),
    { label: 'Investigations', value: investigationsText },
    { label: 'ADT Order', value: adtOrderText },
    { label: 'Pediatric Milestone', value: pediatricMilestoneText },
    { label: "Doctor's Notes", value: doctorNotesText },
  ];

  for (const section of sections) {
    if (section.value?.trim()) {
      const value = section.value.trim();
      // If content has bullet points or multiple lines, put label on its own line
      if (value.includes('•') || value.includes('\n')) {
        lines.push(`${section.label}:`);
        lines.push(value);
      } else {
        lines.push(`${section.label}: ${value}`);
      }
      lines.push('');
    }
  }

  // Ayurveda-only Advice & Follow Up, History, Diagnosis (Ayurvedic/
  // Allopathic), and Treatment (Medicines/Treatment Principle/Treatment
  // Given) — never populated for other departments. Advice & Follow Up is
  // moved up here, before History/Diagnosis/Treatment, matching the fixed
  // Ayurveda case-sheet order (other departments still get it further down).
  if (isAyurveda) {
    const ayurvedaFollowUpPlanText = toProseBulletText(fields.followUpPlan);
    if (ayurvedaFollowUpPlanText) {
      lines.push('Advice & Follow Up:');
      lines.push('');
      lines.push('Follow Up Plan:');
      lines.push(ayurvedaFollowUpPlanText);
      lines.push('');
    }

    if (medicalHistoryText) {
      lines.push('History:');
      lines.push(medicalHistoryText);
      lines.push('');
    }
    if (labInvestigationsReviewedText) {
      lines.push('Lab Investigations Reviewed:');
      lines.push(labInvestigationsReviewedText);
      lines.push('');
    }

    const personalHistoryLines: string[] = [];
    PERSONAL_HISTORY_FIELDS.forEach(({ key, label }) => {
      const value = personalHistory?.[key];
      if (value?.trim()) personalHistoryLines.push(`  ${label}: ${value.trim()}`);
    });
    if (fields.familyHistory?.trim()) {
      personalHistoryLines.push(`  Family History: ${fields.familyHistory.trim()}`);
    }
    if (personalHistoryLines.length > 0) {
      lines.push('Medical & Family History:');
      lines.push(...personalHistoryLines);
      lines.push('');
    }

    const diagnosisLines: string[] = [];
    if (fields.diagnosisAyurvedic?.trim()) {
      diagnosisLines.push(`  Ayurvedic: ${fields.diagnosisAyurvedic.trim()}`);
    }
    if (fields.diagnosisAllopathic?.trim()) {
      diagnosisLines.push(`  Allopathic: ${fields.diagnosisAllopathic.trim()}`);
    }
    if (diagnosisLines.length > 0) {
      lines.push('Diagnosis:');
      lines.push(...diagnosisLines);
      lines.push('');
    }

    const hasMedicinesTable = treatmentMedicinesTable && treatmentMedicinesTable.length > 0;
    const treatmentLines: string[] = [];
    if (hasMedicinesTable) {
      treatmentLines.push('  Medicines:');
      treatmentMedicinesTable!.forEach((med) => {
        const parts = [
          med.name,
          med.form,
          med.dose,
          med.frequency,
          med.duration,
          med.anupana,
          med.timing,
          med.additional_info,
        ].filter(Boolean);
        treatmentLines.push(`    - ${parts.join(', ')}`);
      });
    } else if (fields.treatmentMedicines?.trim()) {
      treatmentLines.push(`  Medicines: ${fields.treatmentMedicines.trim()}`);
    }
    if (fields.treatmentPrinciple?.trim()) {
      treatmentLines.push(`  Treatment Principle: ${fields.treatmentPrinciple.trim()}`);
    }
    // Treatment Given has no text of its own — it's just a heading directly
    // containing its two nested sub-lists, Diet and Lifestyle (same pattern
    // as Diagnosis containing Ayurvedic/Allopathic).
    if (diet && diet.length > 0) {
      treatmentLines.push('  Treatment Given:');
      treatmentLines.push('    Diet:');
      diet.forEach((d) => treatmentLines.push(`      - ${d}`));
    }
    if (lifestyle && lifestyle.length > 0) {
      if (!(diet && diet.length > 0)) treatmentLines.push('  Treatment Given:');
      treatmentLines.push('    Lifestyle:');
      lifestyle.forEach((l) => treatmentLines.push(`      - ${l}`));
    }
    if (treatmentLines.length > 0) {
      lines.push('Treatment:');
      lines.push(...treatmentLines);
      lines.push('');
    }
  }

  // Advice & Follow Up group — dietAdvice and followUpPlan grouped together.
  // Ayurveda's was already pushed above, before History/Diagnosis/Treatment —
  // skip it here to avoid a duplicate.
  if (!isAyurveda) {
    const dietAdvice = toProseBulletText(fields.dietAdvice);
    const followUpPlan = toProseBulletText(fields.followUpPlan);
    if (dietAdvice || followUpPlan) {
      lines.push('Advice & Follow Up:');
      lines.push('');
      if (dietAdvice) {
        lines.push('Diet Advice:');
        lines.push(dietAdvice);
        lines.push('');
      }
      if (followUpPlan) {
        lines.push('Follow Up Plan:');
        lines.push(followUpPlan);
        lines.push('');
      }
    }
  }

  // Medications (rendered as a table). Ayurveda-only: skip — the "Treatment"
  // section above already lists Medicines.
  if (!isAyurveda && fields.prescribedMedications?.length > 0) {
    lines.push('Medications:');
    lines.push('');
    lines.push(formatMedicationsAsTable(fields.prescribedMedications));
    lines.push('');
  }

  // Diet and Lifestyle are now nested under Treatment → Treatment Given
  // above (Ayurveda only) — no separate top-level section here.

  // Viruddha Ahaara (Ayurveda)
  if (viruddhaAhaara && viruddhaAhaara.length > 0) {
    lines.push('Viruddha Ahaara:');
    viruddhaAhaara.forEach((v) => lines.push(`  - ${v}`));
    lines.push('');
  }

  return lines.join('\n');
}

function medicationsToTableSection(
  medications: StructuredMedication[] | undefined
): ConclusionSection | null {
  if (!medications || medications.length === 0) return null;

  const tableData = parseMedicationsToTable(medications);
  if (!tableData) return null;

  return {
    label: 'Medications',
    type: 'table',
    columns: tableData.columns,
    rows: tableData.rows,
    content: tableData.rows
      .map((r) =>
        tableData.columns
          .map((c) => r[c.key])
          .filter(Boolean)
          .join(' | ')
      )
      .join('\n'),
  };
}

function diagnosisCodesToTableSection(
  items: DiagnosisCodeItem[] | undefined
): ConclusionSection | null {
  if (!items || items.length === 0) return null;

  const rows = items.map((item) => ({
    name: item.symptom,
    type: item.type || 'ICD-11',
    code: item.description ? `${item.code} — ${item.description}` : item.code,
  }));

  return {
    label: 'Diagnosis',
    type: 'table',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
      { key: 'code', label: 'Code' },
    ],
    rows,
    content: rows.map((r) => `${r.name} | ${r.type} | ${r.code}`).join('\n'),
  };
}

/**
 * Splits a free-text investigations field into individual entries. The field is
 * usually pre-joined with ", " (when populated from an AI-extracted array, see
 * CaseSheetPage's getSessionFieldValue) but may also use newlines when hand-edited,
 * so both separators are split on. Exported so every place that renders the case
 * sheet's investigations (PDF, on-screen panel, print prescription) stays in sync.
 */
export function splitInvestigationsText(value: string | undefined | null): string[] {
  return (value || '')
    .split(/\n+|,\s*/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Builds structured conclusion sections for a case sheet, matching the same
 * ConclusionSection shape used by generateDiagnosticReportsPDF (via parseConclusion)
 * so both PDFs render medications/diagnosis as tables through the same renderer.
 */
export function buildCaseSheetConclusionSections(
  fields: CaseSheetEditableFields,
  diet?: string[],
  lifestyle?: string[],
  viruddhaAhaara?: string[],
  isAyurveda?: boolean,
  treatmentMedicinesTable?: PrescribedMedicine[],
  personalHistory?: PersonalHistory | null
): ConclusionSection[] {
  const sections: ConclusionSection[] = [];

  // Prose fields (Treatment Plan, Social History, Doctor's Notes, etc.) — split
  // sentence-by-sentence plus per-sentence list-comma (same rules as Medical
  // History minus its "Lab investigations reviewed:" sub-list detection), so a
  // multi-point narrative renders as separate bullets instead of one run-on
  // bullet holding the entire paragraph.
  const pushTextSection = (label: string, value: string | undefined | null) => {
    const items = splitProseToBullets(value);
    if (items.length > 0) {
      sections.push({ label, content: items.join('\n'), type: 'list', items });
    }
  };

  // Renders as a bullet list (one entry per line) instead of a flowing paragraph —
  // matches how the Diagnostic Report PDF renders investigations_orders.
  const pushListSection = (
    label: string,
    value: string | undefined | null,
    splitter: (value: string | undefined | null) => string[] = splitInvestigationsText
  ) => {
    const items = splitter(value);
    if (items.length > 0) {
      sections.push({ label, content: items.join('\n'), type: 'list', items });
    }
  };

  // Medical History is a free-text narrative built from several AI-extracted
  // sentences concatenated together (and sometimes hand-edited), so it can't
  // be split on commas/semicolons alone like the itemized fields above — it
  // needs sentence-aware splitting (so a decimal like "16.4" isn't broken) plus
  // per-sentence list-comma splitting (so "Hypertension, dyslipidemia, asthma"
  // becomes 3 bullets). It also sometimes carries an embedded "Lab
  // investigations reviewed: ..." clause, which is pulled into its own
  // "Lab Investigations Reviewed" section instead of staying mixed into the
  // main bullet list. See splitMedicalNarrativeToBullets for the full rules.
  const pushMedicalHistorySections = (label: string, value: string | undefined | null) => {
    const { mainItems, labItems } = splitMedicalNarrativeToBullets(value);
    if (mainItems.length > 0) {
      sections.push({ label, content: mainItems.join('\n'), type: 'list', items: mainItems });
    }
    if (labItems.length > 0) {
      sections.push({
        label: 'Lab Investigations Reviewed',
        content: labItems.join('\n'),
        type: 'list',
        items: labItems,
      });
    }
  };

  // Patient Summary intentionally hidden in Case Sheet's PDF — it's a
  // Patient-Record-only field (also hidden in Case Sheet's on-screen panel,
  // see useDepartmentConfig.ts).
  pushListSection('Chief Complaint', fields.chiefComplaint, splitCommaSeparatedText);
  pushTextSection('Clinical Symptoms', fields.clinicalSymptoms);

  // Ayurveda-only: Medical/Family History render below as the dedicated
  // "History" / "Medical & Family History" pair instead, in the fixed
  // Ayurveda section order — skip the generic pushes here.
  if (!isAyurveda) {
    pushMedicalHistorySections('Medical History', fields.medicalHistory);
    // One bullet per family member, no leading number — getFamilyHistoryItems
    // re-splits the already-flattened "1. Relation: X..." text a Case Sheet
    // save leaves this field as (see conclusionParser.ts for why).
    pushListSection('Family History', fields.familyHistory, getFamilyHistoryItems);
  }

  pushTextSection('Social History', fields.socialHistory);
  pushListSection('Allergies', fields.allergies, splitCommaSeparatedText);
  pushTextSection('Medication History', fields.medicationHistory);
  pushTextSection('Review of Systems', fields.reviewOfSystems);
  pushListSection('General Examination', fields.generalExamination, splitCommaSeparatedText);

  // Astha Sthaana Pareeksha — Ayurveda-only, right after General Examination,
  // matching the fixed Ayurveda case-sheet order.
  if (isAyurveda) {
    pushTextSection('Astha Sthaana Pareeksha', fields.doshaProfile);
  }

  // Ayurveda-only: skip the generic Assessment/Diagnosis, ICD-11
  // diagnosis-codes table, and Treatment Plan — the Ayurvedic/Allopathic
  // Diagnosis and Treatment sections below replace them.
  if (!isAyurveda) {
    pushListSection('Assessment / Diagnosis', fields.assessmentDiagnosis, splitCommaSeparatedText);
    const diagnosisSection = diagnosisCodesToTableSection(fields.diagnosisCodes);
    if (diagnosisSection) sections.push(diagnosisSection);
    pushTextSection('Treatment Plan', fields.treatmentPlan);
  }

  pushListSection('Investigations', fields.investigations);
  pushTextSection('ADT Order', fields.adtOrder);
  pushTextSection('Pediatric Milestone', fields.pediatricMilestone);
  pushTextSection("Doctor's Notes", fields.doctorNotes);

  // Advice & Follow Up — Ayurveda-only, moved up here (right after Astha
  // Sthaana Pareeksha's generic siblings) so it lands before History/
  // Diagnosis/Treatment, matching the fixed Ayurveda case-sheet order. Other
  // departments still get it further down at its usual spot below.
  if (isAyurveda) {
    const followUpPlanItems = splitProseToBullets(fields.followUpPlan);
    if (followUpPlanItems.length > 0) {
      sections.push({
        label: 'Advice & Follow Up',
        content: `Follow Up Plan:\n${followUpPlanItems.join('\n')}`,
      });
    }
  }

  // Ayurveda-only History (Medical History) / Diagnosis (Ayurvedic/
  // Allopathic) / Treatment (Medicines/Treatment Principle/Treatment
  // Given) — never populated for other departments.
  if (isAyurveda) {
    pushMedicalHistorySections('History', fields.medicalHistory);

    const personalHistoryLines: string[] = [];
    PERSONAL_HISTORY_FIELDS.forEach(({ key, label }) => {
      const value = personalHistory?.[key];
      if (value?.trim()) personalHistoryLines.push(`${label}: ${value.trim()}`);
    });
    if (fields.familyHistory?.trim()) {
      personalHistoryLines.push(`Family History: ${fields.familyHistory.trim()}`);
    }
    if (personalHistoryLines.length > 0) {
      sections.push({
        label: 'Medical & Family History',
        content: personalHistoryLines.join('\n'),
      });
    }

    const ayurvedaDiagnosisLines: string[] = [];
    if (fields.diagnosisAyurvedic?.trim()) {
      ayurvedaDiagnosisLines.push(`Ayurvedic: ${fields.diagnosisAyurvedic.trim()}`);
    }
    if (fields.diagnosisAllopathic?.trim()) {
      ayurvedaDiagnosisLines.push(`Allopathic: ${fields.diagnosisAllopathic.trim()}`);
    }
    if (ayurvedaDiagnosisLines.length > 0) {
      sections.push({ label: 'Ayurvedic Diagnosis', content: ayurvedaDiagnosisLines.join('\n') });
    }

    if (treatmentMedicinesTable && treatmentMedicinesTable.length > 0) {
      const columns = [
        { key: 'name', label: 'Medicine Name' },
        { key: 'form', label: 'Form' },
        { key: 'dose', label: 'Dosage' },
        { key: 'frequency', label: 'Frequency' },
        { key: 'duration', label: 'Duration' },
        { key: 'anupana', label: 'Anupana' },
        { key: 'timing', label: 'Timing' },
        { key: 'additional_info', label: 'Additional Info' },
      ];
      const rows: Array<Record<string, string>> = treatmentMedicinesTable.map((med) =>
        Object.fromEntries(
          columns.map((c) => [c.key, String((med as Record<string, unknown>)[c.key] || '')])
        )
      );
      sections.push({
        label: 'Ayurvedic Treatment — Medicines',
        type: 'table',
        columns,
        rows,
        content: rows
          .map((r) =>
            columns
              .map((c) => r[c.key])
              .filter(Boolean)
              .join(' | ')
          )
          .join('\n'),
      });
    } else {
      pushTextSection('Ayurvedic Treatment — Medicines', fields.treatmentMedicines);
    }

    if (fields.treatmentPrinciple?.trim()) {
      sections.push({
        label: 'Ayurvedic Treatment — Treatment Principle',
        content: fields.treatmentPrinciple.trim(),
      });
    }

    // Treatment Given has no text of its own — same as the Case Sheet's
    // Treatment Given, it's just a heading directly containing its two
    // nested sub-lists, Diet and Lifestyle.
    if ((diet && diet.length > 0) || (lifestyle && lifestyle.length > 0)) {
      const treatmentGivenLines: string[] = [];
      if (diet && diet.length > 0) {
        treatmentGivenLines.push('Diet:');
        diet.forEach((d) => treatmentGivenLines.push(`  - ${d}`));
      }
      if (lifestyle && lifestyle.length > 0) {
        if (diet && diet.length > 0) treatmentGivenLines.push('');
        treatmentGivenLines.push('Lifestyle:');
        lifestyle.forEach((l) => treatmentGivenLines.push(`  - ${l}`));
      }
      sections.push({
        label: 'Ayurvedic Treatment — Treatment Given',
        content: treatmentGivenLines.join('\n'),
      });
    }
  }

  // Ayurveda's Advice & Follow Up was already pushed earlier, positioned
  // before History/Diagnosis/Treatment — skip it here to avoid a duplicate.
  if (!isAyurveda) {
    const dietAdviceItems = splitProseToBullets(fields.dietAdvice);
    const followUpPlanItems = splitProseToBullets(fields.followUpPlan);
    if (dietAdviceItems.length > 0 || followUpPlanItems.length > 0) {
      const content: string[] = [];
      if (dietAdviceItems.length > 0) content.push('Diet Advice:', ...dietAdviceItems);
      if (followUpPlanItems.length > 0) {
        if (dietAdviceItems.length > 0) content.push('');
        content.push('Follow Up Plan:', ...followUpPlanItems);
      }
      sections.push({ label: 'Advice & Follow Up', content: content.join('\n') });
    }
  }

  // Ayurveda-only: skip the generic prescribed-medications table — the
  // "Ayurvedic Treatment — Medicines" section above already covers it.
  if (!isAyurveda) {
    const medicationsSection = medicationsToTableSection(fields.prescribedMedications);
    if (medicationsSection) sections.push(medicationsSection);
  }

  // Diet and Lifestyle are now nested under Treatment → Treatment Given
  // above (Ayurveda only) — no separate top-level section here.

  if (viruddhaAhaara && viruddhaAhaara.length > 0) {
    sections.push({
      label: 'Viruddha Ahaara',
      content: viruddhaAhaara.map((v) => `- ${v}`).join('\n'),
    });
  }

  return sections;
}
